export interface ItemGeometry {
  left: number;
  top: number;
  width: number;
  height: number;
}

export type InlineEditStyle = Record<string, number | string>;

type CodeDataPath = Array<string | number>;

export interface CodeEditableField {
  key: string;
  label: string;
  value: string;
  path: CodeDataPath;
  geometry?: ItemGeometry;
  inlineEditStyle?: InlineEditStyle;
  /** true 면 표시만 하고 편집을 막는다 (예: 인포그래픽 씬 타이밍 — TTS 실측 길이라 편집 시 음성 어긋남). */
  readOnly?: boolean;
  /** 값이 열거형이면 유효 값 목록 — 인스펙터가 자유 텍스트 대신 select 로 렌더한다. */
  options?: readonly string[];
}

const FIELD_KEY_PREFIX = 'data:';
const EDITOR_LAYOUT_VERSION = 'code-data-layout.v1';
const BASE_WIDTH = 1920;
const BASE_HEIGHT = 1080;

const SCENE_PANEL_FIELDS = [
  'badge',
  'category',
  'headline',
  'subtitle',
  'footnote',
] as const;

const SCENE_PANEL_LABELS: Record<(typeof SCENE_PANEL_FIELDS)[number], string> =
  {
    badge: '배지',
    category: '카테고리',
    headline: '헤드라인',
    subtitle: '본문',
    footnote: '푸터',
  };

const PANEL_LAYOUT = {
  left: 1320,
  top: 80,
  width: 560,
  height: 920,
  rowHeight: 56,
  gap: 28,
  headlineFontSize: 48,
  headlineLineHeight: 56.64,
  subtitleFontSize: 24,
  subtitleLineHeight: 36,
  footnoteHeight: 44,
};
const PANEL_FONT = 'Pretendard';
const PANEL_COLORS = {
  bgDeep: '#0A1628',
  bgMid: '#142847',
  red: '#EF4444',
  redHi: '#FCA5A5',
  white: '#F8FAFC',
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  value != null && typeof value === 'object' && !Array.isArray(value);

const asNumber = (value: unknown, fallback: number) => {
  const number = Number(value);

  return Number.isFinite(number) ? number : fallback;
};

const nonEmptyString = (value: unknown, fallback = '') =>
  typeof value === 'string' && value.trim().length > 0 ? value : fallback;

const extractJsonObjectAfterConst = (code: string, constName: string) => {
  const dataIndex = code.indexOf(`const ${constName}`);

  if (dataIndex < 0) {
    return null;
  }

  const openIndex = code.indexOf('{', dataIndex);

  if (openIndex < 0) {
    return null;
  }

  let depth = 0;
  let inString = false;
  let stringQuote = '';
  let escaped = false;

  for (let index = openIndex; index < code.length; index += 1) {
    const char = code[index];

    if (inString) {
      if (escaped) {
        escaped = false;
        continue;
      }

      if (char === '\\') {
        escaped = true;
        continue;
      }

      if (char === stringQuote) {
        inString = false;
        stringQuote = '';
      }

      continue;
    }

    if (char === '"' || char === "'") {
      inString = true;
      stringQuote = char;
      continue;
    }

    if (char === '{') {
      depth += 1;
      continue;
    }

    if (char === '}') {
      depth -= 1;

      if (depth === 0) {
        return {
          jsonText: code.slice(openIndex, index + 1),
          start: openIndex,
          end: index + 1,
        };
      }
    }
  }

  return null;
};

export const extractCodeDataObjectFromCode = (
  code: string,
  constName = 'DATA',
) => {
  const jsonRange = extractJsonObjectAfterConst(code, constName);

  if (!jsonRange) {
    return null;
  }

  try {
    return {
      data: JSON.parse(jsonRange.jsonText) as unknown,
      range: jsonRange,
    };
  } catch {
    return null;
  }
};

export const replaceCodeDataObjectInCode = ({
  code,
  data,
  constName = 'DATA',
}: {
  code: string;
  data: unknown;
  constName?: string;
}) => {
  const extracted = extractCodeDataObjectFromCode(code, constName);

  if (!extracted) {
    return null;
  }

  return `${code.slice(0, extracted.range.start)}${JSON.stringify(data)}${code.slice(extracted.range.end)}`;
};

const fieldKeyFromPath = (path: CodeDataPath) =>
  `${FIELD_KEY_PREFIX}${encodeURIComponent(JSON.stringify(path))}`;

const parseFieldKey = (key: string): CodeDataPath | null => {
  if (!key.startsWith(FIELD_KEY_PREFIX)) {
    return null;
  }

  try {
    const parsed = JSON.parse(
      decodeURIComponent(key.slice(FIELD_KEY_PREFIX.length)),
    ) as unknown;

    if (
      !Array.isArray(parsed) ||
      !parsed.every(
        (part) => typeof part === 'string' || typeof part === 'number',
      )
    ) {
      return null;
    }

    return parsed;
  } catch {
    return null;
  }
};

const isSceneRightPanelPath = (
  path: CodeDataPath,
): path is [
  'scenes',
  number,
  'rightPanel',
  (typeof SCENE_PANEL_FIELDS)[number],
] =>
  path.length === 4 &&
  path[0] === 'scenes' &&
  typeof path[1] === 'number' &&
  path[2] === 'rightPanel' &&
  typeof path[3] === 'string' &&
  (SCENE_PANEL_FIELDS as readonly string[]).includes(path[3]);

const getValueAtPath = (value: unknown, path: CodeDataPath): unknown =>
  path.reduce<unknown>((current, part) => {
    if (Array.isArray(current) && typeof part === 'number') {
      return current[part];
    }

    if (isRecord(current) && typeof part === 'string') {
      return current[part];
    }

    return undefined;
  }, value);

const setValueAtPath = (
  value: unknown,
  path: CodeDataPath,
  nextLeafValue: unknown,
): unknown => {
  if (path.length === 0) {
    return nextLeafValue;
  }

  const [head, ...tail] = path;
  const clone = Array.isArray(value)
    ? [...value]
    : isRecord(value)
      ? { ...value }
      : typeof head === 'number'
        ? []
        : {};

  if (typeof head === 'number' && Array.isArray(clone)) {
    clone[head] = setValueAtPath(clone[head], tail, nextLeafValue);
    return clone;
  }

  if (typeof head === 'string' && !Array.isArray(clone)) {
    clone[head] = setValueAtPath(clone[head], tail, nextLeafValue);
    return clone;
  }

  return value;
};

const removeValueAtPath = (value: unknown, path: CodeDataPath): unknown => {
  if (path.length === 0) {
    return undefined;
  }

  const [head, ...tail] = path;
  const clone = Array.isArray(value)
    ? [...value]
    : isRecord(value)
      ? { ...value }
      : typeof head === 'number'
        ? []
        : {};

  if (tail.length === 0) {
    if (typeof head === 'number' && Array.isArray(clone)) {
      clone.splice(head, 1);
      return clone;
    }

    if (typeof head === 'string' && !Array.isArray(clone)) {
      delete clone[head];
      return clone;
    }

    return clone;
  }

  if (typeof head === 'number' && Array.isArray(clone)) {
    clone[head] = removeValueAtPath(clone[head], tail);
    return clone;
  }

  if (typeof head === 'string' && !Array.isArray(clone)) {
    clone[head] = removeValueAtPath(clone[head], tail);
    return clone;
  }

  return value;
};

const labelFromPath = (path: CodeDataPath) =>
  path.map((part) => (typeof part === 'number' ? `[${part}]` : part)).join('.');

const scaledGeometry = (
  compositionWidth: number,
  compositionHeight: number,
  geometry: ItemGeometry,
): ItemGeometry => ({
  left: Math.round(geometry.left * (compositionWidth / BASE_WIDTH)),
  top: Math.round(geometry.top * (compositionHeight / BASE_HEIGHT)),
  width: Math.round(geometry.width * (compositionWidth / BASE_WIDTH)),
  height: Math.round(geometry.height * (compositionHeight / BASE_HEIGHT)),
});

const geometryFromUnknown = (value: unknown): ItemGeometry | null => {
  if (!isRecord(value)) {
    return null;
  }

  const left = asNumber(value.left, NaN);
  const top = asNumber(value.top, NaN);
  const width = asNumber(value.width, NaN);
  const height = asNumber(value.height, NaN);

  if (
    !Number.isFinite(left) ||
    !Number.isFinite(top) ||
    !Number.isFinite(width) ||
    !Number.isFinite(height) ||
    width <= 0 ||
    height <= 0
  ) {
    return null;
  }

  return { left, top, width, height };
};

const getFieldLayoutGeometry = (
  data: unknown,
  fieldKey: string,
): ItemGeometry | null => {
  if (!isRecord(data) || !isRecord(data.editorLayout)) {
    return null;
  }

  const { editorLayout } = data;

  if (!isRecord(editorLayout.fields)) {
    return null;
  }

  return geometryFromUnknown(editorLayout.fields[fieldKey]);
};

const isFieldHidden = (data: unknown, fieldKey: string) => {
  if (!isRecord(data) || !isRecord(data.editorLayout)) {
    return false;
  }

  const { editorLayout } = data;

  return (
    isRecord(editorLayout.hiddenFields) &&
    editorLayout.hiddenFields[fieldKey] === true
  );
};

const normalizeGeometryForBase = ({
  geometry,
  compositionWidth,
  compositionHeight,
}: {
  geometry: ItemGeometry;
  compositionWidth: number;
  compositionHeight: number;
}): ItemGeometry => ({
  left: Math.round(
    geometry.left * (BASE_WIDTH / Math.max(1, compositionWidth)),
  ),
  top: Math.round(
    geometry.top * (BASE_HEIGHT / Math.max(1, compositionHeight)),
  ),
  width: Math.max(
    1,
    Math.round(geometry.width * (BASE_WIDTH / Math.max(1, compositionWidth))),
  ),
  height: Math.max(
    1,
    Math.round(
      geometry.height * (BASE_HEIGHT / Math.max(1, compositionHeight)),
    ),
  ),
});

const estimateLineCount = ({
  text,
  fontSize,
  width,
}: {
  text: string;
  fontSize: number;
  width: number;
}) => {
  const normalizedText = text.trim();

  if (!normalizedText) {
    return 1;
  }

  const weightedLength = Array.from(normalizedText).reduce((sum, char) => {
    if (/\s/.test(char)) {
      return sum + 0.35;
    }

    if (/[\u3131-\uD79D]/u.test(char)) {
      return sum + 1;
    }

    if (/[A-Z0-9]/u.test(char)) {
      return sum + 0.72;
    }

    return sum + 0.55;
  }, 0);
  const charactersPerLine = Math.max(1, width / (fontSize * 0.9));

  return Math.max(1, Math.ceil(weightedLength / charactersPerLine));
};

const getScenePanelGeometry = ({
  field,
  headline,
  subtitle,
}: {
  field: (typeof SCENE_PANEL_FIELDS)[number];
  headline: string;
  subtitle: string;
}): ItemGeometry => {
  const headlineLines = estimateLineCount({
    text: headline,
    fontSize: PANEL_LAYOUT.headlineFontSize,
    width: PANEL_LAYOUT.width,
  });
  const subtitleLines = estimateLineCount({
    text: subtitle,
    fontSize: PANEL_LAYOUT.subtitleFontSize,
    width: PANEL_LAYOUT.width,
  });
  const headlineHeight = Math.ceil(
    headlineLines * PANEL_LAYOUT.headlineLineHeight,
  );
  const subtitleHeight = Math.ceil(
    subtitleLines * PANEL_LAYOUT.subtitleLineHeight,
  );
  const headlineTop =
    PANEL_LAYOUT.top + PANEL_LAYOUT.rowHeight + PANEL_LAYOUT.gap;
  const subtitleTop = headlineTop + headlineHeight + PANEL_LAYOUT.gap;

  if (field === 'badge') {
    return {
      left: PANEL_LAYOUT.left,
      top: PANEL_LAYOUT.top,
      width: 68,
      height: PANEL_LAYOUT.rowHeight,
    };
  }

  if (field === 'category') {
    return {
      left: PANEL_LAYOUT.left + 80,
      top: PANEL_LAYOUT.top,
      width: PANEL_LAYOUT.width - 80,
      height: PANEL_LAYOUT.rowHeight,
    };
  }

  if (field === 'headline') {
    return {
      left: PANEL_LAYOUT.left,
      top: headlineTop,
      width: PANEL_LAYOUT.width,
      height: Math.max(64, headlineHeight),
    };
  }

  if (field === 'subtitle') {
    return {
      left: PANEL_LAYOUT.left,
      top: subtitleTop,
      width: PANEL_LAYOUT.width,
      height: Math.max(72, subtitleHeight),
    };
  }

  return {
    left: PANEL_LAYOUT.left,
    top: PANEL_LAYOUT.top + PANEL_LAYOUT.height - PANEL_LAYOUT.footnoteHeight,
    width: 360,
    height: PANEL_LAYOUT.footnoteHeight,
  };
};

const getScenePanelInlineEditStyle = (
  field: (typeof SCENE_PANEL_FIELDS)[number],
): InlineEditStyle => {
  const base = {
    fontFamily: PANEL_FONT,
    border: 0,
    outline: 0,
    resize: 'none',
    whiteSpace: 'normal',
    overflow: 'hidden',
    overflowWrap: 'normal',
    wordBreak: 'normal',
  };

  if (field === 'badge') {
    return {
      ...base,
      background: PANEL_COLORS.red,
      color: PANEL_COLORS.white,
      borderRadius: 14,
      fontSize: 24,
      fontWeight: 900,
      lineHeight: '56px',
      textAlign: 'center',
      boxShadow: '0 6px 24px rgba(239,68,68,0.45)',
    };
  }

  if (field === 'category') {
    return {
      ...base,
      background: 'rgba(20,40,71,0.95)',
      color: PANEL_COLORS.white,
      borderRadius: 12,
      border: '1px solid rgba(239,68,68,0.4)',
      fontSize: 20,
      fontWeight: 800,
      letterSpacing: '0.12em',
      lineHeight: '50px',
      padding: '0 22px',
    };
  }

  if (field === 'headline') {
    return {
      ...base,
      background: PANEL_COLORS.bgDeep,
      color: PANEL_COLORS.white,
      fontSize: 48,
      fontWeight: 900,
      lineHeight: 1.18,
      textShadow: '0 4px 24px rgba(0,0,0,0.5)',
    };
  }

  if (field === 'subtitle') {
    return {
      ...base,
      background: PANEL_COLORS.bgDeep,
      color: PANEL_COLORS.redHi,
      fontSize: 24,
      fontWeight: 500,
      lineHeight: 1.5,
    };
  }

  return {
    ...base,
    background: PANEL_COLORS.bgMid,
    color: PANEL_COLORS.white,
    borderRadius: 10,
    border: '1px solid rgba(239,68,68,0.18)',
    fontSize: 14,
    fontWeight: 800,
    letterSpacing: '0.16em',
    lineHeight: '44px',
    padding: '0 20px',
  };
};

const sceneStartFrame = (scene: Record<string, unknown>, fps: number) =>
  Math.max(0, Math.round(asNumber(scene.startSec, 0) * fps));

const sceneDurationFrames = (scene: Record<string, unknown>, fps: number) => {
  const durationSec = asNumber(
    scene.durationSec,
    Math.max(0.5, asNumber(scene.endSec, 0) - asNumber(scene.startSec, 0)),
  );

  return Math.max(1, Math.round(durationSec * fps));
};

const findActiveSceneIndex = (
  scenes: Array<Record<string, unknown>>,
  frame: number,
  fps: number,
) => {
  if (scenes.length === 0) {
    return -1;
  }

  for (let index = 0; index < scenes.length; index += 1) {
    const scene = scenes[index];

    if (!scene) {
      continue;
    }

    const startFrame = sceneStartFrame(scene, fps);
    const endFrame = startFrame + sceneDurationFrames(scene, fps);

    if (frame < endFrame || index === scenes.length - 1) {
      return index;
    }
  }

  return scenes.length - 1;
};

const scenePanelValue = (
  scene: Record<string, unknown>,
  sceneIndex: number,
  totalScenes: number,
  field: (typeof SCENE_PANEL_FIELDS)[number],
) => {
  const panel = isRecord(scene.rightPanel) ? scene.rightPanel : {};
  const pageIndex = asNumber(scene.pageIndex, 0);

  if (field === 'badge') {
    return nonEmptyString(panel.badge, String(sceneIndex + 1).padStart(2, '0'));
  }
  if (field === 'category') {
    return nonEmptyString(
      panel.category,
      sceneIndex + 1 === totalScenes ? 'BOTTOM LINE' : 'EVIDENCE',
    );
  }
  if (field === 'headline') {
    return nonEmptyString(
      panel.headline,
      String(scene.caption || scene.text || `Scene ${sceneIndex + 1}`),
    );
  }
  if (field === 'subtitle') {
    return nonEmptyString(
      panel.subtitle,
      String(scene.text || scene.caption || ''),
    );
  }

  return nonEmptyString(
    panel.footnote,
    `${String(scene.regionId || 'PDF')} · PAGE ${pageIndex + 1}`,
  );
};

const isShinhanPdfTemplateData = (data: unknown) => {
  if (!isRecord(data)) {
    return false;
  }

  const version = typeof data.version === 'string' ? data.version : '';
  const template = isRecord(data.template) ? data.template : {};
  const templateId =
    typeof template.templateId === 'string' ? template.templateId : '';

  return version.includes('shinhan_pdf') || templateId.includes('shinhan-pdf');
};

const getSceneRightPanelFields = ({
  data,
  compositionWidth,
  compositionHeight,
  sceneIndexFilter,
}: {
  data: unknown;
  compositionWidth: number;
  compositionHeight: number;
  sceneIndexFilter?: number;
}): CodeEditableField[] => {
  if (!isRecord(data) || !Array.isArray(data.scenes)) {
    return [];
  }

  const scenes = data.scenes.flatMap((scene) => {
    const record = isRecord(scene) ? scene : null;
    return record ? [record] : [];
  });
  const supportsSyntheticPanels = isShinhanPdfTemplateData(data);

  if (
    scenes.length === 0 ||
    (!scenes.some((scene) => isRecord(scene.rightPanel)) &&
      !supportsSyntheticPanels)
  ) {
    return [];
  }

  return scenes.flatMap((scene, sceneIndex) => {
    if (sceneIndexFilter != null && sceneIndex !== sceneIndexFilter) {
      return [];
    }

    if (!supportsSyntheticPanels && !isRecord(scene.rightPanel)) {
      return [];
    }

    const headline = scenePanelValue(
      scene,
      sceneIndex,
      scenes.length,
      'headline',
    );
    const subtitle = scenePanelValue(
      scene,
      sceneIndex,
      scenes.length,
      'subtitle',
    );

    return SCENE_PANEL_FIELDS.flatMap((field) => {
      const path: CodeDataPath = ['scenes', sceneIndex, 'rightPanel', field];
      const key = fieldKeyFromPath(path);

      if (isFieldHidden(data, key)) {
        return [];
      }

      const geometry =
        getFieldLayoutGeometry(data, key) ??
        getScenePanelGeometry({ field, headline, subtitle });

      return [
        {
          key,
          label: `${String(sceneIndex + 1).padStart(2, '0')} · ${SCENE_PANEL_LABELS[field]}`,
          value: scenePanelValue(scene, sceneIndex, scenes.length, field),
          path,
          geometry: scaledGeometry(
            compositionWidth,
            compositionHeight,
            geometry,
          ),
          inlineEditStyle: getScenePanelInlineEditStyle(field),
        },
      ];
    });
  });
};

const getScenePanelBaseGeometryForPath = (
  data: unknown,
  path: CodeDataPath,
): ItemGeometry | null => {
  if (
    !isSceneRightPanelPath(path) ||
    !isRecord(data) ||
    !Array.isArray(data.scenes)
  ) {
    return null;
  }

  const scenes = data.scenes.flatMap((scene) => {
    const record = isRecord(scene) ? scene : null;
    return record ? [record] : [];
  });
  const scene = scenes[path[1]];

  if (!scene) {
    return null;
  }

  return getScenePanelGeometry({
    field: path[3],
    headline: scenePanelValue(scene, path[1], scenes.length, 'headline'),
    subtitle: scenePanelValue(scene, path[1], scenes.length, 'subtitle'),
  });
};

const shouldSkipGenericStringField = (path: CodeDataPath, value: string) => {
  const last = String(path[path.length - 1] ?? '').toLowerCase();

  if (/^(https?:|data:)/i.test(value)) {
    return true;
  }

  if (/(url|uri|s3|bucket|key|audio|image|src|thumbnail|font)$/i.test(last)) {
    return true;
  }

  return value.length > 500;
};

const collectGenericStringFields = (
  value: unknown,
  path: CodeDataPath = [],
): CodeEditableField[] => {
  if (typeof value === 'string') {
    if (!value.trim() || shouldSkipGenericStringField(path, value)) {
      return [];
    }

    return [
      {
        key: fieldKeyFromPath(path),
        label: labelFromPath(path),
        value,
        path,
      },
    ];
  }

  if (Array.isArray(value)) {
    return value.flatMap((item, index) =>
      collectGenericStringFields(item, [...path, index]),
    );
  }

  if (isRecord(value)) {
    return Object.entries(value).flatMap(([key, item]) =>
      collectGenericStringFields(item, [...path, key]),
    );
  }

  return [];
};

// ─── 인포그래픽 (const SCENES) 편집 ────────────────────────────────────────────
// AGENT_STORYBOARD_INFOGRAPHIC 코드는 shinhan `const DATA` 와 동일한 데이터-구동 구조를
// `const SCENES = { s1: { start, dur, …, content: { elements: [{ content, grade, pattern,
// revealAccent }] } } }` 로 갖는다. 아래 범용부는 shinhan 과 같은 field-path read/replace
// 메커니즘을 SCENES 에 겨눠, 씬별 요소 텍스트·스타일·타이밍을 인스펙터에서 편집하게 한다.
// 레이아웃/애니메이션은 코드가 소유(수정 안 함) — 좌표 계산·native 아이템 분해는 하지 않는다.
const INFOGRAPHIC_SCENES_MARKER = 'infographic.scenes.v1';
const INFOGRAPHIC_SCENES_CONST = 'SCENES';
const INFOGRAPHIC_ELEMENT_STYLE_FIELDS = [
  'grade',
  'pattern',
  'revealAccent',
] as const;
const INFOGRAPHIC_STYLE_LABELS: Record<
  (typeof INFOGRAPHIC_ELEMENT_STYLE_FIELDS)[number],
  string
> = {
  grade: '등급',
  pattern: '패턴',
  revealAccent: '등장 강조',
};
// 스타일 필드 유효 값 vocab (ai-api MSB §3-4/§9-2 카탈로그 미러) — 인스펙터 select 옵션이자
// 업데이트 검증 기준. 자유 텍스트 오타(예: "boxslider")가 조용히 렌더를 깨는 것을 차단한다.
const INFOGRAPHIC_STYLE_OPTIONS: Record<
  (typeof INFOGRAPHIC_ELEMENT_STYLE_FIELDS)[number],
  readonly string[]
> = {
  grade: ['A★', 'A', 'B', 'C'],
  pattern: [
    '',
    'underlineSweep',
    'swipeHighlight',
    'boxSliderHighlight',
    'scaleStamp',
    'particleRise',
    'confetti',
  ],
  revealAccent: ['', 'glow_pulse', 'particle_burst', 'ring_ripple'],
};
const INFOGRAPHIC_TIMING_FIELDS = ['start', 'dur'] as const;
const INFOGRAPHIC_TIMING_LABELS: Record<
  (typeof INFOGRAPHIC_TIMING_FIELDS)[number],
  string
> = {
  start: '시작(초)',
  dur: '길이(초)',
};

/**
 * code 클립이 편집 가능한 인포그래픽(SCENES)인지 식별한다. ai-api infographic_agent_v2 가
 * 주입하는 미참조 마커 상수(INFOGRAPHIC_SCENES_SCHEMA = "infographic.scenes.v1")로 판별한다
 * — shinhan 이 DATA.version/templateId 로 식별하는 방식과 동일 패턴.
 */
export const isInfographicScenesCode = (code: string) =>
  code.includes(INFOGRAPHIC_SCENES_MARKER) && code.includes('const SCENES');

const infographicSceneKeys = (data: Record<string, unknown>) =>
  Object.keys(data)
    .filter((key) => /^s\d+$/.test(key) && isRecord(data[key]))
    .sort((a, b) => Number(a.slice(1)) - Number(b.slice(1)));

const getInfographicSceneFields = (
  data: unknown,
  sceneKeyFilter?: string,
): CodeEditableField[] => {
  if (!isRecord(data)) {
    return [];
  }

  return infographicSceneKeys(data).flatMap((sceneKey) => {
    if (sceneKeyFilter != null && sceneKey !== sceneKeyFilter) {
      return [];
    }

    const scene = data[sceneKey];

    if (!isRecord(scene)) {
      return [];
    }

    const sceneLabel = sceneKey.toUpperCase();
    const fields: CodeEditableField[] = [];

    INFOGRAPHIC_TIMING_FIELDS.forEach((field) => {
      if (typeof scene[field] !== 'number') {
        return;
      }

      const path: CodeDataPath = [sceneKey, field];
      fields.push({
        key: fieldKeyFromPath(path),
        label: `${sceneLabel} · ${INFOGRAPHIC_TIMING_LABELS[field]}`,
        value: String(scene[field]),
        path,
        // 타이밍은 TTS 실측 길이가 정본 — 편집하면 음성과 어긋나고 후속 씬 start 재계산이
        // 필요하므로 v1 은 표시 전용(형 확정 2026-07-20). 편집 확장은 후속 task.
        readOnly: true,
      });
    });

    const content = isRecord(scene.content) ? scene.content : null;
    const elements =
      content && Array.isArray(content.elements) ? content.elements : [];

    elements.forEach((element, elementIndex) => {
      if (!isRecord(element)) {
        return;
      }

      const elementLabel = `${sceneLabel} · 요소 ${elementIndex + 1}`;

      if (typeof element.content === 'string') {
        const path: CodeDataPath = [
          sceneKey,
          'content',
          'elements',
          elementIndex,
          'content',
        ];
        fields.push({
          key: fieldKeyFromPath(path),
          label: `${elementLabel} 텍스트`,
          value: element.content,
          path,
        });
      }

      INFOGRAPHIC_ELEMENT_STYLE_FIELDS.forEach((styleField) => {
        if (typeof element[styleField] !== 'string') {
          return;
        }

        const path: CodeDataPath = [
          sceneKey,
          'content',
          'elements',
          elementIndex,
          styleField,
        ];
        fields.push({
          key: fieldKeyFromPath(path),
          label: `${elementLabel} ${INFOGRAPHIC_STYLE_LABELS[styleField]}`,
          value: element[styleField] as string,
          path,
          options: INFOGRAPHIC_STYLE_OPTIONS[styleField],
        });
      });
    });

    return fields;
  });
};

const findActiveInfographicSceneKey = (
  data: Record<string, unknown>,
  frame: number,
  fps: number,
): string | null => {
  const keys = infographicSceneKeys(data);

  if (keys.length === 0) {
    return null;
  }

  for (let index = 0; index < keys.length; index += 1) {
    const sceneKey = keys[index] as string;
    const scene = data[sceneKey];

    if (!isRecord(scene)) {
      continue;
    }

    const startFrame = Math.max(0, Math.round(asNumber(scene.start, 0) * fps));
    const durationFrames = Math.max(
      1,
      Math.round(asNumber(scene.dur, asNumber(scene.duration, 0)) * fps),
    );

    if (frame < startFrame + durationFrames || index === keys.length - 1) {
      return sceneKey;
    }
  }

  return keys[keys.length - 1] ?? null;
};

// 타이밍 편집 시 dur/duration/end 별칭을 정합 유지한다 (ai-api 가 dur===duration, end=start+dur
// 로 방출 — 별칭이 어긋나면 컴포넌트가 참조하는 duration/end 가 start/dur 과 불일치해 재생 깨짐).
const alignInfographicSceneTiming = (
  data: unknown,
  path: CodeDataPath,
): unknown => {
  if (
    path.length !== 2 ||
    typeof path[0] !== 'string' ||
    !/^s\d+$/.test(path[0])
  ) {
    return data;
  }

  const timingKey = path[1];

  if (
    timingKey !== 'start' &&
    timingKey !== 'dur' &&
    timingKey !== 'duration'
  ) {
    return data;
  }

  const scene = getValueAtPath(data, [path[0]]);

  if (!isRecord(scene)) {
    return data;
  }

  const start = asNumber(scene.start, NaN);
  const dur = asNumber(
    timingKey === 'duration' ? scene.duration : scene.dur,
    NaN,
  );

  if (!Number.isFinite(start) || !Number.isFinite(dur)) {
    return data;
  }

  let next = setValueAtPath(data, [path[0], 'dur'], dur);
  next = setValueAtPath(next, [path[0], 'duration'], dur);
  next = setValueAtPath(next, [path[0], 'end'], start + dur);

  return next;
};

const updateInfographicSceneField = (
  code: string,
  path: CodeDataPath,
  value: string,
) => {
  // 타이밍(start/dur)은 읽기 전용 — TTS 실측 길이 정본 보호 (인스펙터 disable 의 방어선).
  if (
    path.length === 2 &&
    (INFOGRAPHIC_TIMING_FIELDS as readonly unknown[]).includes(path[1])
  ) {
    return null;
  }

  // 스타일 필드는 vocab 검증 — select 우회(직접 코드 경로 등)로 온 오타 값이 렌더를 깨지 않게.
  const styleField =
    path.length === 5 && path[2] === 'elements' ? path[4] : null;
  if (
    typeof styleField === 'string' &&
    styleField in INFOGRAPHIC_STYLE_OPTIONS &&
    !INFOGRAPHIC_STYLE_OPTIONS[
      styleField as keyof typeof INFOGRAPHIC_STYLE_OPTIONS
    ].includes(value)
  ) {
    return null;
  }

  const extracted = extractCodeDataObjectFromCode(
    code,
    INFOGRAPHIC_SCENES_CONST,
  );

  if (!extracted) {
    return null;
  }

  const previousValue = getValueAtPath(extracted.data, path);
  const nextLeafValue =
    typeof previousValue === 'number' && Number.isFinite(Number(value))
      ? Number(value)
      : value;
  const nextData = alignInfographicSceneTiming(
    setValueAtPath(extracted.data, path, nextLeafValue),
    path,
  );

  return replaceCodeDataObjectInCode({
    code,
    data: nextData,
    constName: INFOGRAPHIC_SCENES_CONST,
  });
};

export const getCodeEditableFields = ({
  code,
  compositionWidth,
  compositionHeight,
}: {
  code: string;
  compositionWidth: number;
  compositionHeight: number;
}) => {
  if (isInfographicScenesCode(code)) {
    const extracted = extractCodeDataObjectFromCode(
      code,
      INFOGRAPHIC_SCENES_CONST,
    );

    return extracted ? getInfographicSceneFields(extracted.data) : [];
  }

  const extracted = extractCodeDataObjectFromCode(code);

  if (!extracted) {
    return [];
  }

  const panelFields = getSceneRightPanelFields({
    data: extracted.data,
    compositionWidth,
    compositionHeight,
  });

  if (panelFields.length === 0) {
    return collectGenericStringFields(extracted.data);
  }

  if (isShinhanPdfTemplateData(extracted.data)) {
    return panelFields;
  }

  return [
    ...panelFields,
    ...collectGenericStringFields(extracted.data).filter(
      (field) =>
        !(
          field.path.length >= 3 &&
          field.path[0] === 'scenes' &&
          typeof field.path[1] === 'number' &&
          field.path[2] === 'rightPanel'
        ),
    ),
  ];
};

export const getActiveCodeEditableFields = ({
  code,
  compositionWidth,
  compositionHeight,
  frame,
  fps,
}: {
  code: string;
  compositionWidth: number;
  compositionHeight: number;
  frame: number;
  fps: number;
}) => {
  if (isInfographicScenesCode(code)) {
    const extracted = extractCodeDataObjectFromCode(
      code,
      INFOGRAPHIC_SCENES_CONST,
    );

    if (!extracted || !isRecord(extracted.data)) {
      return [];
    }

    const activeSceneKey = findActiveInfographicSceneKey(
      extracted.data,
      frame,
      fps,
    );

    return activeSceneKey
      ? getInfographicSceneFields(extracted.data, activeSceneKey)
      : [];
  }

  const extracted = extractCodeDataObjectFromCode(code);

  if (
    !extracted ||
    !isRecord(extracted.data) ||
    !Array.isArray(extracted.data.scenes)
  ) {
    return [];
  }

  const scenes = extracted.data.scenes.flatMap((scene) => {
    const record = isRecord(scene) ? scene : null;
    return record ? [record] : [];
  });
  const sceneIndex = findActiveSceneIndex(scenes, frame, fps);

  if (sceneIndex < 0) {
    return [];
  }

  return getSceneRightPanelFields({
    data: extracted.data,
    compositionWidth,
    compositionHeight,
    sceneIndexFilter: sceneIndex,
  });
};

const RIGHT_PANEL_LAYOUT_RUNTIME = `
const CODE_DATA_RIGHT_PANEL_ORIGIN = {left: 1320, top: 80};
const codeDataFieldKeyFromPath = (path) => \`${FIELD_KEY_PREFIX}\${encodeURIComponent(JSON.stringify(path))}\`;
const codeDataAsNumber = (value, fallback) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
};
const getCodeDataRightPanelLayout = (sceneIndex, field) => {
  const layout = DATA.editorLayout?.fields?.[codeDataFieldKeyFromPath(['scenes', sceneIndex, 'rightPanel', field])];
  if (!layout) {
    return null;
  }
  const left = codeDataAsNumber(layout.left, NaN);
  const top = codeDataAsNumber(layout.top, NaN);
  const width = codeDataAsNumber(layout.width, NaN);
  const height = codeDataAsNumber(layout.height, NaN);
  if (![left, top, width, height].every(Number.isFinite)) {
    return null;
  }
  const base = layout.base || {};
  const baseLeft = codeDataAsNumber(base.left, left);
  const baseTop = codeDataAsNumber(base.top, top);
  return {left, top, width, height, baseLeft, baseTop};
};
const getCodeDataRightPanelHidden = (sceneIndex, field) => (
  DATA.editorLayout?.hiddenFields?.[codeDataFieldKeyFromPath(['scenes', sceneIndex, 'rightPanel', field])] === true
);
const appendCodeDataTransform = (style, transform) => {
  const existingTransform = typeof style.transform === 'string' ? style.transform : '';
  return existingTransform ? \`\${existingTransform} \${transform}\` : transform;
};
const withRightPanelFieldLayout = (sceneIndex, field, style) => {
  if (getCodeDataRightPanelHidden(sceneIndex, field)) {
    return {...style, display: 'none'};
  }
  const layout = getCodeDataRightPanelLayout(sceneIndex, field);
  if (!layout) {
    return style;
  }
  const translateX = layout.left - layout.baseLeft;
  const translateY = layout.top - layout.baseTop;
  return {
    ...style,
    width: layout.width,
    height: layout.height,
    boxSizing: 'border-box',
    transform: appendCodeDataTransform(style, \`translate(\${translateX}px, \${translateY}px)\`),
    transformOrigin: 'top left',
  };
};
`;

const removeExistingRightPanelLayoutRuntime = (code: string) => {
  const runtimeStart = code.indexOf('const CODE_DATA_RIGHT_PANEL_ORIGIN =');

  if (runtimeStart < 0) {
    return code;
  }

  const layerStart = code.indexOf('function ContinuousPdfLayer', runtimeStart);

  if (layerStart < 0) {
    return code;
  }

  return `${code.slice(0, runtimeStart)}${code.slice(layerStart)}`;
};

const findJsxExpressionEnd = (code: string, openIndex: number) => {
  let depth = 0;
  let inString = false;
  let stringQuote = '';
  let escaped = false;

  for (let index = openIndex; index < code.length; index += 1) {
    const char = code[index];

    if (inString) {
      if (escaped) {
        escaped = false;
        continue;
      }

      if (char === '\\') {
        escaped = true;
        continue;
      }

      if (char === stringQuote) {
        inString = false;
        stringQuote = '';
      }

      continue;
    }

    if (char === '"' || char === "'" || char === '`') {
      inString = true;
      stringQuote = char;
      continue;
    }

    if (char === '{') {
      depth += 1;
      continue;
    }

    if (char === '}') {
      depth -= 1;

      if (depth === 0) {
        return index + 1;
      }
    }
  }

  return -1;
};

const replaceStyleBeforePanelExpression = ({
  code,
  field,
}: {
  code: string;
  field: (typeof SCENE_PANEL_FIELDS)[number];
}) => {
  const contentIndex = code.indexOf(`{panel.${field}}`);

  if (contentIndex < 0) {
    return code;
  }

  const styleIndex = code.lastIndexOf('style={{', contentIndex);

  if (styleIndex < 0) {
    return code;
  }

  const expressionStart = styleIndex + 'style='.length;
  const expressionEnd = findJsxExpressionEnd(code, expressionStart);

  if (expressionEnd < 0 || expressionEnd > contentIndex) {
    return code;
  }

  const expression = code.slice(expressionStart, expressionEnd);
  const objectExpression = expression.slice(1, -1);
  const nextStyle = `style={withRightPanelFieldLayout(index, '${field}', ${objectExpression})}`;

  return `${code.slice(0, styleIndex)}${nextStyle}${code.slice(expressionEnd)}`;
};

const maskCommentsAndStrings = (code: string) => {
  let masked = '';
  let inString = false;
  let stringQuote = '';
  let escaped = false;
  let inLineComment = false;
  let inBlockComment = false;

  for (let index = 0; index < code.length; index += 1) {
    const char = code[index];
    const nextChar = code[index + 1];

    if (inLineComment) {
      if (char === '\n') {
        inLineComment = false;
        masked += char;
      } else {
        masked += ' ';
      }
      continue;
    }

    if (inBlockComment) {
      if (char === '*' && nextChar === '/') {
        inBlockComment = false;
        masked += '  ';
        index += 1;
      } else {
        masked += char === '\n' ? char : ' ';
      }
      continue;
    }

    if (inString) {
      if (escaped) {
        escaped = false;
        masked += ' ';
        continue;
      }

      if (char === '\\') {
        escaped = true;
        masked += ' ';
        continue;
      }

      if (char === stringQuote) {
        inString = false;
        stringQuote = '';
      }

      masked += char === '\n' ? char : ' ';
      continue;
    }

    if (char === '/' && nextChar === '/') {
      inLineComment = true;
      masked += '  ';
      index += 1;
      continue;
    }

    if (char === '/' && nextChar === '*') {
      inBlockComment = true;
      masked += '  ';
      index += 1;
      continue;
    }

    if (char === '"' || char === "'" || char === '`') {
      inString = true;
      stringQuote = char;
      masked += ' ';
      continue;
    }

    masked += char;
  }

  return masked;
};

const getContinuousPdfLayerBody = (code: string) => {
  const functionIndex = code.indexOf('function ContinuousPdfLayer');

  if (functionIndex < 0) {
    return null;
  }

  const bodyStart = code.indexOf('{', functionIndex);

  if (bodyStart < 0) {
    return null;
  }

  const bodyEnd = findJsxExpressionEnd(code, bodyStart);

  if (bodyEnd < 0) {
    return null;
  }

  return code.slice(bodyStart + 1, bodyEnd - 1);
};

const hasContinuousPdfLayerIndexBinding = (code: string) => {
  const body = getContinuousPdfLayerBody(code);

  if (!body) {
    return false;
  }

  const panelExpressionIndex = body.indexOf('{panel.');
  const bodyBeforePanel =
    panelExpressionIndex < 0 ? body : body.slice(0, panelExpressionIndex);
  const maskedBody = maskCommentsAndStrings(bodyBeforePanel);
  let depth = 0;
  let declaration = '';
  let isCollectingDeclaration = false;

  for (let index = 0; index < maskedBody.length; index += 1) {
    const char = maskedBody[index] ?? '';

    if (
      depth === 0 &&
      !isCollectingDeclaration &&
      /\S/.test(char) &&
      /^(?:const|let|var)\b/.test(maskedBody.slice(index))
    ) {
      isCollectingDeclaration = true;
      declaration = '';
    }

    if (isCollectingDeclaration) {
      declaration += char;
    }

    if (char === '{' || char === '[' || char === '(') {
      depth += 1;
    } else if (char === '}' || char === ']' || char === ')') {
      depth = Math.max(0, depth - 1);
    }

    if (isCollectingDeclaration && char === ';' && depth === 0) {
      if (
        /\b(?:const|let|var)\s+index\b/.test(declaration) ||
        /\b(?:const|let|var)\s+\{[^;]*\bindex\b[^;]*\}/.test(declaration)
      ) {
        return true;
      }

      isCollectingDeclaration = false;
      declaration = '';
    }
  }

  return false;
};

const ensureSceneRightPanelLayoutRuntime = (
  code: string,
  path: CodeDataPath,
) => {
  if (!isSceneRightPanelPath(path)) {
    return code;
  }

  const codeWithoutRuntime = removeExistingRightPanelLayoutRuntime(code);
  const insertIndex = codeWithoutRuntime.indexOf('function ContinuousPdfLayer');

  if (
    insertIndex < 0 ||
    !hasContinuousPdfLayerIndexBinding(codeWithoutRuntime)
  ) {
    return null;
  }

  const codeWithReplacements = SCENE_PANEL_FIELDS.reduce(
    (currentCode, field) =>
      currentCode.includes(`withRightPanelFieldLayout(index, '${field}'`)
        ? currentCode
        : replaceStyleBeforePanelExpression({
            code: currentCode,
            field,
          }),
    codeWithoutRuntime,
  );

  if (!codeWithReplacements.includes('withRightPanelFieldLayout(index,')) {
    return null;
  }

  return `${codeWithReplacements.slice(0, insertIndex)}${RIGHT_PANEL_LAYOUT_RUNTIME}\n${codeWithReplacements.slice(insertIndex)}`;
};

export const repairCodeDataRightPanelLayoutRuntime = (code: string) => {
  if (!code.includes('withRightPanelFieldLayout(index,')) {
    return code;
  }

  return (
    ensureSceneRightPanelLayoutRuntime(code, [
      'scenes',
      0,
      'rightPanel',
      'subtitle',
    ]) ?? code
  );
};

export const updateCodeEditableField = (
  code: string,
  fieldKey: string,
  value: string,
) => {
  const path = parseFieldKey(fieldKey);

  if (!path) {
    return null;
  }

  if (isInfographicScenesCode(code)) {
    return updateInfographicSceneField(code, path, value);
  }

  const extracted = extractCodeDataObjectFromCode(code);

  if (!extracted) {
    return null;
  }

  const previousValue = getValueAtPath(extracted.data, path);
  const nextValue =
    typeof previousValue === 'number' && Number.isFinite(Number(value))
      ? Number(value)
      : value;

  return replaceCodeDataObjectInCode({
    code,
    data: setValueAtPath(extracted.data, path, nextValue),
  });
};

export const updateCodeEditableFieldGeometry = ({
  code,
  fieldKey,
  geometry,
  compositionWidth,
  compositionHeight,
}: {
  code: string;
  fieldKey: string;
  geometry: ItemGeometry;
  compositionWidth: number;
  compositionHeight: number;
}) => {
  const path = parseFieldKey(fieldKey);
  const extracted = extractCodeDataObjectFromCode(code);

  if (!path || !extracted) {
    return null;
  }

  let nextData = setValueAtPath(
    extracted.data,
    ['editorLayout', 'version'],
    EDITOR_LAYOUT_VERSION,
  );
  nextData = setValueAtPath(nextData, ['editorLayout', 'base'], {
    width: BASE_WIDTH,
    height: BASE_HEIGHT,
  });
  nextData = setValueAtPath(nextData, ['editorLayout', 'fields', fieldKey], {
    ...normalizeGeometryForBase({
      geometry,
      compositionWidth,
      compositionHeight,
    }),
    base: getScenePanelBaseGeometryForPath(extracted.data, path) ?? undefined,
  });

  const nextCode = replaceCodeDataObjectInCode({
    code,
    data: nextData,
  });

  return nextCode ? ensureSceneRightPanelLayoutRuntime(nextCode, path) : null;
};

export const deleteCodeEditableField = (code: string, fieldKey: string) => {
  const path = parseFieldKey(fieldKey);
  const extracted = extractCodeDataObjectFromCode(code);

  if (!path || !extracted) {
    return null;
  }

  if (isSceneRightPanelPath(path)) {
    let nextData = setValueAtPath(
      extracted.data,
      ['editorLayout', 'version'],
      EDITOR_LAYOUT_VERSION,
    );
    nextData = setValueAtPath(nextData, ['editorLayout', 'base'], {
      width: BASE_WIDTH,
      height: BASE_HEIGHT,
    });
    nextData = setValueAtPath(
      nextData,
      ['editorLayout', 'hiddenFields', fieldKey],
      true,
    );

    const nextCode = replaceCodeDataObjectInCode({
      code,
      data: nextData,
    });

    return nextCode ? ensureSceneRightPanelLayoutRuntime(nextCode, path) : null;
  }

  return replaceCodeDataObjectInCode({
    code,
    data: removeValueAtPath(extracted.data, path),
  });
};
